import {
  Avatar,
  colors,
  DropdownItem,
  DropdownMenu,
  Icon,
  IconButton,
  SkeletonTable,
  SkeletonText,
  Table,
  Tag,
  type TableColumn,
} from '@dev-spendesk/grapes';
import classNames from 'classnames';
import { type Dispatch, type SetStateAction } from 'react';

import { unwrapQuery } from 'src/core/api/unwrapQuery';
import { EllipsisTooltip } from 'src/core/common/components/EllipsisTooltip';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { useOrganisationCompanyList } from 'src/core/modules/organisation-reporting/hooks/data/useOrganisationCompanyList';

import {
  useOrganisationMembers,
  type Member,
} from '../../hooks/useOrganisationMembers';
import { type Setting } from '../../setting';

export const SettingsTable = ({
  settings,
  onEditCostCenter,
  onAssignCompanies,
  onDeleteCostCenter,
  setSelectedRows,
  selectedRows,
}: {
  settings: Setting[];
  onEditCostCenter: (setting: Setting) => void;
  onAssignCompanies: (setting: Setting) => void;
  onDeleteCostCenter: (setting: Setting) => void;
  setSelectedRows: Dispatch<SetStateAction<Setting[]>>;
  selectedRows: Setting[];
}) => {
  const { t } = useTranslation('global');
  const organisationCompanies = useOrganisationCompanyList();
  const organisationMembers = useOrganisationMembers();
  const members = unwrapQuery(organisationMembers);

  if (members === undefined) {
    return <LoadingSkeleton />;
  }

  const columns: TableColumn<Setting>[] = [
    {
      id: 'name',
      header: t('multiEntityHub.costCenters.table.header.name'),
      renderCell: (setting) => setting.name,
    },
    {
      id: 'owner',
      header: t('multiEntityHub.costCenters.table.header.owner'),
      renderCell: (setting) => (
        <div className="flex items-center gap-8">
          <Avatar size={24} text={getOwnerName(members, setting.ownerId)} />
          {getOwnerName(members, setting.ownerId)}
        </div>
      ),
    },
    {
      id: 'type',
      header: t('multiEntityHub.costCenters.table.header.type'),
      renderCell: (setting) => (
        <Tag
          variant={setting.type === 'cross-entity' ? 'purple' : 'ocean'}
          iconName={
            setting.type === 'cross-entity'
              ? 'buildings-office'
              : 'building-storefront'
          }
        >
          {setting.type === 'cross-entity'
            ? t('multiEntityHub.costCenters.table.type.cross-entity')
            : t('multiEntityHub.costCenters.table.type.entity-specific')}
        </Tag>
      ),
    },
    {
      id: 'appliesTo',
      header: t('multiEntityHub.costCenters.table.header.appliesTo'),
      renderCell: (setting) => {
        return (
          <EllipsisTooltip
            text={setting.companyIds
              .map(
                (companyId) =>
                  organisationCompanies.find(
                    (company) => company.id === companyId,
                  )?.name,
              )
              .sort((a, b) => a?.localeCompare(b ?? '') ?? 0)
              .join(', ')}
          />
        );
      },
    },
    {
      id: 'actions',
      header: t('multiEntityHub.costCenters.table.header.actions'),
      width: 88,
      renderCell: (setting) => (
        <DropdownMenu
          placement="bottom-end"
          options={[
            {
              label: t('multiEntityHub.costCenters.table.action.edit'),
              key: 'editCostCenter',
            },
            {
              label: t(
                'multiEntityHub.costCenters.table.action.assignCompanies',
              ),
              key: 'assignCompanies',
            },
            {
              label: t('multiEntityHub.costCenters.table.action.delete'),
              key: 'deleteCostCenter',
            },
          ]}
          renderButton={(getToggleButtonProps) => (
            <IconButton
              {...getToggleButtonProps()}
              variant="tertiaryNeutral"
              aria-label="Actions"
              iconName="ellipsis-vertical"
            />
          )}
          renderOption={(option) => (
            <DropdownItem
              label={option.label}
              key={option.key}
              prefix={getOptionPrefix(option.key)}
              className={classNames(
                getOptionClassName(option.key),
                'min-w-[290px]',
              )}
            />
          )}
          onSelect={(option) => {
            if (option.key === 'editCostCenter') {
              onEditCostCenter(setting);
            }
            if (option.key === 'assignCompanies') {
              onAssignCompanies(setting);
            }
            if (option.key === 'deleteCostCenter') {
              onDeleteCostCenter(setting);
            }
          }}
        />
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      data={settings}
      getRowId={(row) => row.key}
      emptyState={{
        title: t('multiEntityHub.costCenters.noCostCentersFound'),
      }}
      selectedRowIds={selectedRows.map((option) => option.key)}
      onRowSelectionChange={(row, id, checked) => {
        setSelectedRows((options) => {
          if (checked) {
            return options.concat(row);
          }
          return options.filter((option) => option.key !== id);
        });
      }}
      onAllRowsSelectionChange={(rows, _, checked) => {
        setSelectedRows(checked ? rows : []);
      }}
    />
  );
};

const LoadingSkeleton = () => {
  const columns = [
    {
      cell: <SkeletonText width="100%" />,
      header: <SkeletonText width="100%" />,
    },
    {
      cell: <SkeletonText width="100%" />,
      header: <SkeletonText width="100%" />,
    },
    {
      cell: <SkeletonText width="100%" />,
      header: <SkeletonText width="100%" />,
    },
    {
      cell: <SkeletonText width="100%" />,
      header: <SkeletonText width="100%" />,
    },
    {
      cell: <SkeletonText width="10%" />,
      header: <SkeletonText width="10%" />,
    },
  ];
  return (
    <SkeletonTable
      columns={columns}
      numberOfRows={10}
      rowHeight="normal"
      withHeader
    />
  );
};

const getOwnerName = (members: Member[] | undefined, ownerId: string) => {
  const owner = members?.find((member) => member.id === ownerId);
  if (!owner) {
    return '';
  }
  if (owner.isPending) {
    return owner.email;
  }
  return owner.fullname;
};

const getOptionPrefix = (key: string) => {
  switch (key) {
    case 'editCostCenter':
      return <Icon name="pen" />;
    case 'assignCompanies':
      return <Icon name="arrow-left-right" />;
    case 'deleteCostCenter':
      return <Icon color={colors.contentWarningDefault} name="trash" />;
    default:
      return null;
  }
};

const getOptionClassName = (key: string) => {
  switch (key) {
    case 'deleteCostCenter':
      return 'text-content-warning-default';
    default:
      return '';
  }
};
